import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Plus, FileText, CheckCircle, Clock, AlertCircle, Send, Eye, EyeOff, Trash2, Filter, X, DollarSign, RefreshCw, Link2, Copy, Repeat, CalendarClock, Archive, ArchiveRestore, Trash, MoreVertical, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format currency with commas: 20000 -> "20,000"
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const InvoicesPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { invoiceId: urlInvoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [paymentLoadingId, setPaymentLoadingId] = useState(null); // Track which invoice is being paid
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [listUpdateKey, setListUpdateKey] = useState(0); // Force list re-render when invoice viewed
  
  // Filters
  const [filterProject, setFilterProject] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSubscription, setFilterSubscription] = useState('all'); // all, active, inactive
  const [invoiceView, setInvoiceView] = useState('active'); // active, archived, trash
  
  // Form state for creating invoices
  const [formData, setFormData] = useState({
    client_id: '',
    project_id: '',
    description: '',
    due_date: '',
    line_items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }],
    is_recurring: false,
    recurring_interval: '',
    recurring_end_date: ''
  });
  
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isOtherServices, setIsOtherServices] = useState(false);
  const [filteredProjects, setFilteredProjects] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build query params for filters
      let queryParams = new URLSearchParams();
      if (filterProject !== 'all') queryParams.append('project_id', filterProject);
      if (filterClient !== 'all') queryParams.append('client_id', filterClient);
      // Don't send past_due to backend - we'll filter client-side
      if (filterStatus !== 'all' && filterStatus !== 'past_due') {
        queryParams.append('status', filterStatus);
      }
      // Add view parameter for archive/trash filtering
      queryParams.append('view', invoiceView);
      
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      
      const requests = [
        axios.get(`${API}/invoices${queryString}`, { headers }),
        axios.get(`${API}/projects`, { headers })
      ];
      
      // Only fetch clients for admin
      if (user?.role === 'admin') {
        requests.push(axios.get(`${API}/clients`, { headers }));
      }
      
      const responses = await Promise.all(requests);
      let invoiceData = responses[0].data;
      
      // Filter for past_due client-side (based on is_past_due field)
      if (filterStatus === 'past_due') {
        invoiceData = invoiceData.filter(inv => inv.is_past_due === true);
      }
      
      // Filter by subscription status client-side
      if (filterSubscription === 'active') {
        invoiceData = invoiceData.filter(inv => inv.is_recurring && inv.subscription_status === 'active');
      } else if (filterSubscription === 'inactive') {
        invoiceData = invoiceData.filter(inv => inv.is_recurring && inv.subscription_status === 'cancelled');
      }
      
      setInvoices(invoiceData);
      setProjects(responses[1].data);
      if (responses[2]) {
        setClients(responses[2].data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [token, user, filterProject, filterClient, filterStatus, filterSubscription, invoiceView]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh invoices every 10 seconds for real-time updates
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchData();
    }, 10000); // 10 seconds

    return () => clearInterval(refreshInterval);
  }, [fetchData]);
  
  // Handle payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    
    if (payment === 'cancelled') {
      toast.info('Payment was cancelled.');
      navigate('/dashboard/invoices', { replace: true });
      return;
    }
    
    if (sessionId && payment === 'success') {
      setPaymentProcessing(true);
      
      // Define pollPaymentStatus inside the effect to avoid dependency issues
      const pollStatus = async (sid, attempts = 0) => {
        const maxAttempts = 10;
        const pollInterval = 2000;

        if (attempts >= maxAttempts) {
          setPaymentProcessing(false);
          // Show success anyway for test mode - Stripe test sessions don't always update immediately
          toast.success('Payment submitted! Invoice status will update shortly.');
          fetchData();
          navigate('/dashboard/invoices', { replace: true });
          return;
        }

        try {
          const response = await axios.get(`${API}/payments/status/${sid}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = response.data;
          
          console.log('Payment status check:', data);

          // Check for paid status OR complete session status
          if (data.payment_status === 'paid' || data.status === 'complete') {
            setPaymentProcessing(false);
            setShowPaymentSuccess(true);
            fetchData();
            navigate('/dashboard/invoices', { replace: true });
            return;
          } else if (data.status === 'expired') {
            setPaymentProcessing(false);
            toast.error('Payment session expired. Please try again.');
            navigate('/dashboard/invoices', { replace: true });
            return;
          }

          setTimeout(() => pollStatus(sid, attempts + 1), pollInterval);
        } catch (error) {
          console.error('Error checking payment status:', error);
          // Continue polling even on error
          setTimeout(() => pollStatus(sid, attempts + 1), pollInterval);
        }
      };
      
      pollStatus(sessionId);
    }
  }, [searchParams, token, fetchData, navigate]);

  // Handle deep link to specific invoice
  useEffect(() => {
    if (urlInvoiceId && invoices.length > 0 && !loading) {
      const invoice = invoices.find(inv => inv.id === urlInvoiceId);
      if (invoice) {
        setSelectedInvoice(invoice);
        setShowDetailModal(true);
        // Mark invoice as viewed when opening from notification link (only for non-admin users)
        if (!invoice.viewed_at && invoice.status === 'sent' && user?.role !== 'admin') {
          axios.post(`${API}/invoices/${urlInvoiceId}/view`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          }).then((response) => {
            // Immediately update local state
            const viewedAt = response.data.viewed_at || new Date().toISOString();
            const updatedInvoice = { ...invoice, viewed_at: viewedAt };
            setInvoices(prevInvoices => {
              const newInvoices = prevInvoices.map(inv => 
                inv.id === urlInvoiceId ? updatedInvoice : inv
              );
              return [...newInvoices];
            });
            setSelectedInvoice(updatedInvoice);
            setListUpdateKey(prev => prev + 1);
          }).catch(error => {
            console.error('Failed to mark invoice as viewed:', error);
          });
        }
      } else {
        // Invoice might be in a different view (archived/trash), try to fetch it directly
        const fetchInvoice = async () => {
          try {
            const response = await axios.get(`${API}/invoices/${urlInvoiceId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data) {
              setSelectedInvoice(response.data);
              setShowDetailModal(true);
              // Mark invoice as viewed (only for non-admin users)
              if (!response.data.viewed_at && response.data.status === 'sent' && user?.role !== 'admin') {
                const viewResponse = await axios.post(`${API}/invoices/${urlInvoiceId}/view`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                const viewedAt = viewResponse.data.viewed_at || new Date().toISOString();
                setSelectedInvoice(prev => prev ? { ...prev, viewed_at: viewedAt } : prev);
                setListUpdateKey(prev => prev + 1);
              }
            }
          } catch (error) {
            console.error('Failed to fetch invoice:', error);
            toast.error('Invoice not found');
            navigate('/dashboard/invoices');
          }
        };
        fetchInvoice();
      }
    }
  }, [urlInvoiceId, invoices, loading, token, navigate]);

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setSelectedClient(client);
    setSelectedProject(null);
    setIsOtherServices(false);
    
    // Filter projects for this client
    const clientProjects = projects.filter(p => p.client_id === clientId);
    setFilteredProjects(clientProjects);
    
    setFormData({ 
      ...formData, 
      client_id: clientId, 
      project_id: '' 
    });
  };

  const handleProjectSelect = (projectId) => {
    if (projectId === 'other_services') {
      setIsOtherServices(true);
      setSelectedProject(null);
      setFormData({ ...formData, project_id: '' });
    } else {
      const project = projects.find(p => p.id === projectId);
      setSelectedProject(project);
      setIsOtherServices(false);
      setFormData({ ...formData, project_id: projectId });
    }
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...formData.line_items];
    newItems[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    
    // Calculate amount
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].amount = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setFormData({ ...formData, line_items: newItems });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: '', quantity: 1, unit_price: 0, amount: 0 }]
    });
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length > 1) {
      const newItems = formData.line_items.filter((_, i) => i !== index);
      setFormData({ ...formData, line_items: newItems });
    }
  };

  const calculateTotal = () => {
    return formData.line_items.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    
    if (!formData.project_id && !isOtherServices) {
      toast.error('Please select a project or choose "Other Services"');
      return;
    }
    
    const total = calculateTotal();
    if (total <= 0) {
      toast.error('Invoice total must be greater than 0');
      return;
    }
    
    try {
      const submitData = {
        client_id: formData.client_id,
        project_id: isOtherServices ? null : formData.project_id,
        description: formData.description,
        due_date: formData.due_date,
        line_items: formData.line_items,
        amount: total,
        invoice_type: isOtherServices ? 'other_services' : 'project',
        is_recurring: formData.is_recurring,
        recurring_interval: formData.is_recurring ? formData.recurring_interval : null
      };
      
      await axios.post(`${API}/invoices`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice created successfully');
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to create invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      project_id: '',
      description: '',
      due_date: '',
      line_items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }],
      is_recurring: false,
      recurring_interval: '',
      recurring_end_date: ''
    });
    setSelectedClient(null);
    setSelectedProject(null);
    setIsOtherServices(false);
    setFilteredProjects([]);
  };

  // Edit invoice functions
  const [editFormData, setEditFormData] = useState({
    description: '',
    due_date: '',
    line_items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }],
    is_recurring: false,
    recurring_interval: '',
    recurring_end_date: ''
  });

  const openEditModal = (invoice) => {
    setEditingInvoice(invoice);
    setEditFormData({
      description: invoice.description || '',
      due_date: invoice.due_date ? invoice.due_date.split('T')[0] : '',
      line_items: invoice.line_items && invoice.line_items.length > 0 
        ? invoice.line_items.map(item => ({
            description: item.description || '',
            quantity: item.quantity || 1,
            unit_price: item.unit_price || 0,
            amount: item.amount || 0
          }))
        : [{ description: invoice.description || '', quantity: 1, unit_price: invoice.amount || 0, amount: invoice.amount || 0 }],
      is_recurring: invoice.is_recurring || false,
      recurring_interval: invoice.recurring_interval || '',
      recurring_end_date: invoice.recurring_end_date ? invoice.recurring_end_date.split('T')[0] : ''
    });
    setShowEditModal(true);
  };

  const updateEditLineItem = (index, field, value) => {
    const newItems = [...editFormData.line_items];
    newItems[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    
    // Calculate amount
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].amount = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setEditFormData({ ...editFormData, line_items: newItems });
  };

  const addEditLineItem = () => {
    setEditFormData({
      ...editFormData,
      line_items: [...editFormData.line_items, { description: '', quantity: 1, unit_price: 0, amount: 0 }]
    });
  };

  const removeEditLineItem = (index) => {
    if (editFormData.line_items.length > 1) {
      const newItems = editFormData.line_items.filter((_, i) => i !== index);
      setEditFormData({ ...editFormData, line_items: newItems });
    }
  };

  const calculateEditTotal = () => {
    return editFormData.line_items.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingInvoice) return;
    
    const total = calculateEditTotal();
    if (total <= 0) {
      toast.error('Invoice total must be greater than 0');
      return;
    }
    
    try {
      const updateData = {
        description: editFormData.description,
        due_date: editFormData.due_date || null,
        line_items: editFormData.line_items,
        is_recurring: editFormData.is_recurring,
        recurring_interval: editFormData.is_recurring ? editFormData.recurring_interval : null,
        recurring_end_date: editFormData.is_recurring && editFormData.recurring_end_date ? editFormData.recurring_end_date : null
      };
      
      await axios.put(`${API}/invoices/${editingInvoice.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice updated successfully');
      setShowEditModal(false);
      setEditingInvoice(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    }
  };

  const sendInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice sent successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to send invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    }
  };

  const resendInvoice = async (invoiceId) => {
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/resend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || 'Invoice reminder sent successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to resend invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend invoice');
    }
  };

  const copyPaymentLink = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/payment-link`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await navigator.clipboard.writeText(response.data.payment_link);
      toast.success(`Payment link copied to clipboard!`);
    } catch (error) {
      console.error('Failed to copy payment link:', error);
      // Fallback: construct link manually if API fails
      const fallbackLink = `${BACKEND_URL.replace('/api', '')}/dashboard/invoices?invoice=${invoiceId}`;
      try {
        await navigator.clipboard.writeText(fallbackLink);
        toast.success('Payment link copied to clipboard!');
      } catch (clipboardError) {
        toast.error('Failed to copy link. Please try again.');
      }
    }
  };

  const updateInvoiceStatus = async (invoiceId, status) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invoice marked as ${status}`);
      fetchData();
    } catch (error) {
      console.error('Failed to update invoice status:', error);
      toast.error('Failed to update invoice status');
    }
  };

  const deleteInvoice = async (invoiceId) => {
    if (!window.confirm('Move this invoice to trash?')) return;
    
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice moved to trash');
      fetchData();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const archiveInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice archived');
      fetchData();
    } catch (error) {
      console.error('Failed to archive invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to archive invoice');
    }
  };

  const restoreInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice restored');
      fetchData();
    } catch (error) {
      console.error('Failed to restore invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore invoice');
    }
  };

  const markAsPaid = async (invoiceId) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/status?status=paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice marked as paid');
      fetchData();
    } catch (error) {
      console.error('Failed to mark invoice as paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const permanentlyDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Permanently delete this invoice? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/invoices/${invoiceId}/permanent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice permanently deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to permanently delete invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to permanently delete invoice');
    }
  };

  // Helper function to mark invoice as viewed - ONLY for user actions, not admin
  // Eye icon darkens for admin when USER views/interacts with invoice
  const markInvoiceAsViewed = async (invoice) => {
    // Only mark as viewed if:
    // 1. Invoice exists and hasn't been viewed yet
    // 2. Invoice is in 'sent' status
    // 3. Current user is NOT an admin (only user actions should mark as viewed)
    if (!invoice || invoice.viewed_at || invoice.status !== 'sent') return;
    if (user?.role === 'admin') return; // Admin actions don't mark as viewed
    
    try {
      const response = await axios.post(`${API}/invoices/${invoice.id}/view`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const viewedAt = response.data.viewed_at || new Date().toISOString();
      const updatedInvoice = { ...invoice, viewed_at: viewedAt };
      
      // Update the invoices list immediately
      setInvoices(prevInvoices => {
        const newInvoices = prevInvoices.map(inv => 
          inv.id === invoice.id ? { ...inv, viewed_at: viewedAt } : inv
        );
        return [...newInvoices];
      });
      
      // Update selected invoice if it's the same one
      setSelectedInvoice(prev => prev?.id === invoice.id ? updatedInvoice : prev);
      
      // Force list re-render
      setListUpdateKey(prev => prev + 1);
      
      return updatedInvoice;
    } catch (error) {
      console.error('Failed to mark invoice as viewed:', error);
      return invoice;
    }
  };

  const payInvoice = async (invoiceId) => {
    // Mark as viewed when user clicks pay (not admin)
    const invoice = invoices.find(inv => inv.id === invoiceId);
    if (invoice) await markInvoiceAsViewed(invoice);
    
    setPaymentLoadingId(invoiceId);
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/pay`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Failed to initiate payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setPaymentLoadingId(null);
    }
  };

  const viewInvoiceDetail = async (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailModal(true);
    
    // Mark invoice as viewed when user clicks (not admin)
    await markInvoiceAsViewed(invoice);
  };

  // Get display status for users:
  // - "Past Due" if invoice is past due date
  // - "Seen" if invoice has been viewed
  // - "New" if invoice is sent but not viewed
  // Admin always sees actual status
  const getDisplayStatus = (invoice) => {
    const status = invoice.status;
    
    // Admin sees actual status
    if (user?.role === 'admin') {
      return status;
    }
    
    // For non-admin users
    if (status === 'paid') {
      return 'paid';
    }
    
    // Check if past due (unpaid and past due date)
    if (invoice.is_past_due && status !== 'paid') {
      return 'past_due';
    }
    
    // Check if viewed
    if (status === 'sent' && invoice.viewed_at) {
      return 'seen';
    }
    
    // New invoice (sent but not viewed)
    if (status === 'sent') {
      return 'new';
    }
    
    return status;
  };

  const getStatusIcon = (invoice) => {
    const displayStatus = typeof invoice === 'string' ? invoice : getDisplayStatus(invoice);
    switch (displayStatus) {
      case 'paid':
        return <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />;
      case 'new':
        return <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />;
      case 'seen':
        return <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />;
      case 'sent':
        return <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />;
      case 'past_due':
      case 'overdue':
        return <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />;
      default:
        return <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />;
    }
  };

  const getStatusClass = (invoice) => {
    const displayStatus = typeof invoice === 'string' ? invoice : getDisplayStatus(invoice);
    switch (displayStatus) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'seen':
        return 'bg-purple-100 text-purple-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'past_due':
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (invoice) => {
    const displayStatus = typeof invoice === 'string' ? invoice : getDisplayStatus(invoice);
    switch (displayStatus) {
      case 'paid':
        return 'Paid';
      case 'new':
        return 'New';
      case 'seen':
        return 'Seen';
      case 'sent':
        return 'Sent';
      case 'past_due':
        return 'Past Due';
      case 'overdue':
        return 'Overdue';
      case 'draft':
        return 'Draft';
      default:
        return displayStatus;
    }
  };

  // Get the "seen" indicator for invoice - shows if user has viewed the invoice
  // EyeOff icon = not seen, Eye icon = seen
  const getSeenIndicator = (invoice) => {
    const isSeen = !!invoice.viewed_at;
    
    // Only show for sent invoices (not draft or paid)
    if (invoice.status === 'draft') {
      return null;
    }
    
    return (
      <div 
        className="flex items-center" 
        title={isSeen ? `Viewed on ${new Date(invoice.viewed_at).toLocaleDateString('en-US')}` : 'Not yet viewed'}
        data-testid={`invoice-seen-indicator-${invoice.id}`}
      >
        {isSeen ? (
          <Eye className="h-4 w-4 text-fearless-blue" data-testid="seen-icon-filled" />
        ) : (
          <EyeOff className="h-4 w-4 text-slate-300" data-testid="seen-icon-outline" />
        )}
      </div>
    );
  };

  const clearFilters = () => {
    setFilterProject('all');
    setFilterClient('all');
    setFilterStatus('all');
    setFilterSubscription('all');
  };

  const hasActiveFilters = filterProject !== 'all' || filterClient !== 'all' || filterStatus !== 'all' || filterSubscription !== 'all';

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64" data-testid="invoices-loading">
          <div className="text-xl text-slate-gray">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);
  const paidAmount = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.amount, 0);
  const pendingAmount = totalAmount - paidAmount;

  return (
    <DashboardLayout>
      {/* Payment Processing Overlay */}
      {paymentProcessing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="payment-processing-overlay">
          <Card className="max-w-md mx-4 shadow-2xl">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-fearless-blue mx-auto mb-6"></div>
              <h2 className="text-xl font-bold text-carbon-black mb-2">Processing Payment</h2>
              <p className="text-slate-gray">Please wait while we confirm your payment...</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payment Success Dialog */}
      <Dialog open={showPaymentSuccess} onOpenChange={setShowPaymentSuccess}>
        <DialogContent className="max-w-md">
          <div className="text-center py-6">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-carbon-black mb-2">Payment Successful!</DialogTitle>
            </DialogHeader>
            <p className="text-slate-gray mb-6">
              Thank you for your payment. Your invoice has been marked as paid.
            </p>
            <Button 
              className="gradient-fearless text-white px-8"
              onClick={() => setShowPaymentSuccess(false)}
            >
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="space-y-6 sm:space-y-8" data-testid="invoices-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-carbon-black mb-1 sm:mb-2" data-testid="invoices-title">Invoices</h1>
            <p className="text-sm sm:text-base text-slate-gray">{user?.role === 'admin' ? 'Manage project invoices' : 'View and pay invoices'}</p>
          </div>
          {user?.role === 'admin' && (
            <Dialog open={showModal} onOpenChange={(open) => { setShowModal(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="gradient-fearless text-white w-full sm:w-auto" data-testid="create-invoice-btn">
                  <Plus className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                  Create Invoice
                </Button>
              </DialogTrigger>

        {/* View Tabs - Admin Only */}
        {user?.role === 'admin' && (
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
            <button
              onClick={() => setInvoiceView('active')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                invoiceView === 'active'
                  ? 'bg-white text-carbon-black shadow-sm'
                  : 'text-slate-gray hover:text-carbon-black'
              }`}
              data-testid="view-active-btn"
            >
              <FileText className="h-4 w-4 inline mr-1.5" />
              Active
            </button>
            <button
              onClick={() => setInvoiceView('archived')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                invoiceView === 'archived'
                  ? 'bg-white text-carbon-black shadow-sm'
                  : 'text-slate-gray hover:text-carbon-black'
              }`}
              data-testid="view-archived-btn"
            >
              <Archive className="h-4 w-4 inline mr-1.5" />
              Archived
            </button>
            <button
              onClick={() => setInvoiceView('trash')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                invoiceView === 'trash'
                  ? 'bg-white text-carbon-black shadow-sm'
                  : 'text-slate-gray hover:text-carbon-black'
              }`}
              data-testid="view-trash-btn"
            >
              <Trash className="h-4 w-4 inline mr-1.5" />
              Trash
            </button>
          </div>
        )}

              <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-lg sm:text-xl">Create New Invoice</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="invoice-form">
                  {/* Client Selection (Required First) */}
                  <div>
                    <label className="block text-sm font-semibold text-carbon-black mb-2">Client *</label>
                    <Select value={formData.client_id} onValueChange={handleClientSelect}>
                      <SelectTrigger data-testid="invoice-client-select" className="text-sm">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id} className="text-sm">
                            {client.name} {client.company ? `(${client.company})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Auto-populated Client Info (Read-only) */}
                  {selectedClient && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 space-y-1">
                      <p className="text-xs text-blue-600 font-medium uppercase tracking-wide">Client Info</p>
                      {selectedClient.company && (
                        <p className="text-sm text-blue-800"><strong>Company:</strong> {selectedClient.company}</p>
                      )}
                      <p className="text-sm text-blue-800"><strong>Email:</strong> {selectedClient.email}</p>
                      {selectedClient.phone && (
                        <p className="text-sm text-blue-800"><strong>Phone:</strong> {selectedClient.phone}</p>
                      )}
                    </div>
                  )}
                  
                  {/* Project Selection (Filtered by Client) */}
                  <div>
                    <label className="block text-sm font-semibold text-carbon-black mb-2">Project</label>
                    <Select 
                      value={isOtherServices ? 'other_services' : formData.project_id} 
                      onValueChange={handleProjectSelect}
                      disabled={!selectedClient}
                    >
                      <SelectTrigger data-testid="invoice-project-select" className={`text-sm ${!selectedClient ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <SelectValue placeholder={selectedClient ? "Select project or Other Services" : "Select a client first"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="other_services" className="text-sm text-slate-gray italic">
                          Other Services (no project)
                        </SelectItem>
                        {filteredProjects.length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs text-slate-gray border-t mt-1">Client Projects</div>
                            {filteredProjects.map((project) => (
                              <SelectItem key={project.id} value={project.id} className="text-sm">
                                {project.project_number ? `${project.project_number} - ${project.title}` : project.title}
                              </SelectItem>
                            ))}
                          </>
                        )}
                        {filteredProjects.length === 0 && selectedClient && (
                          <div className="px-2 py-2 text-xs text-slate-gray italic">No projects for this client</div>
                        )}
                      </SelectContent>
                    </Select>
                    {selectedClient && !formData.project_id && !isOtherServices && (
                      <p className="text-xs text-slate-gray mt-1">Select a project or choose &quot;Other Services&quot; for adhoc invoices</p>
                    )}
                  </div>
                  
                  {/* Selected Project/Type Indicator */}
                  {(selectedProject || isOtherServices) && (
                    <div className={`p-2 rounded-lg border text-sm ${isOtherServices ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-green-50 border-green-200 text-green-800'}`}>
                      {isOtherServices ? (
                        <span>📋 Invoice Type: <strong>Other Services</strong> (not linked to a project)</span>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>📁 Project:</span>
                          {selectedProject?.project_number && (
                            <span className="px-2 py-0.5 rounded bg-fearless-blue/20 text-fearless-blue text-xs font-mono font-semibold" data-testid="invoice-project-number">
                              {selectedProject.project_number}
                            </span>
                          )}
                          <strong>{selectedProject?.title}</strong>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Invoice Template Dropdown */}
                  <div>
                    <label className="block text-sm font-semibold text-carbon-black mb-2">Quick Template (Optional)</label>
                    <Select 
                      value=""
                      onValueChange={(templateId) => {
                        if (!templateId || templateId === 'custom') return;
                        
                        const templates = {
                          // Essentials
                          'essentials-monthly-hosting': {
                            description: 'Essentials Hosting (Monthly)',
                            line_items: [{ description: 'Essentials Hosting (Monthly)', quantity: 1, unit_price: '', amount: 0 }],
                            is_recurring: true,
                            recurring_interval: 'monthly'
                          },
                          'essentials-monthly-support': {
                            description: 'Essentials Support (Monthly)',
                            line_items: [{ description: 'Essentials Support (Monthly)', quantity: 1, unit_price: '', amount: 0 }],
                            is_recurring: true,
                            recurring_interval: 'monthly'
                          },
                          'essentials-monthly-hosting-support': {
                            description: 'Essentials Hosting + Support (Monthly)',
                            line_items: [
                              { description: 'Essentials Hosting (Monthly)', quantity: 1, unit_price: '', amount: 0 },
                              { description: 'Essentials Support (Monthly)', quantity: 1, unit_price: '', amount: 0 }
                            ],
                            is_recurring: true,
                            recurring_interval: 'monthly'
                          },
                          'essentials-yearly-hosting': {
                            description: 'Essentials Hosting (Yearly)',
                            line_items: [{ description: 'Essentials Hosting (Yearly)', quantity: 1, unit_price: '', amount: 0 }],
                            is_recurring: true,
                            recurring_interval: 'yearly'
                          },
                          'essentials-yearly-support': {
                            description: 'Essentials Support (Yearly)',
                            line_items: [{ description: 'Essentials Support (Yearly)', quantity: 1, unit_price: '', amount: 0 }],
                            is_recurring: true,
                            recurring_interval: 'yearly'
                          },
                          'essentials-yearly-hosting-support': {
                            description: 'Essentials Hosting + Support (Yearly)',
                            line_items: [
                              { description: 'Essentials Hosting (Yearly)', quantity: 1, unit_price: '', amount: 0 },
                              { description: 'Essentials Support (Yearly)', quantity: 1, unit_price: '', amount: 0 }
                            ],
                            is_recurring: true,
                            recurring_interval: 'yearly'
                          },
                          // Plus
                          'plus-monthly-hosting': {
                            description: 'Plus Hosting (Monthly)',
                            line_items: [{ description: 'Plus Hosting (Monthly)', quantity: 1, unit_price: '', amount: 0 }],
                            is_recurring: true,
                            recurring_interval: 'monthly'
                          },
                          'plus-monthly-support': {
                            description: 'Plus Support (Monthly)',
                            line_items: [{ description: 'Plus Support (Monthly)', quantity: 1, unit_price: '', amount: 0 }],
                            is_recurring: true,
                            recurring_interval: 'monthly'
                          },
                          'plus-monthly-hosting-support': {
                            description: 'Plus Hosting + Support (Monthly)',
                            line_items: [
                              { description: 'Plus Hosting (Monthly)', quantity: 1, unit_price: '', amount: 0 },
                              { description: 'Plus Support (Monthly)', quantity: 1, unit_price: '', amount: 0 }
                            ],
                            is_recurring: true,
                            recurring_interval: 'monthly'
                          },
                          'plus-yearly-hosting': {
                            description: 'Plus Hosting (Yearly)',
                            line_items: [{ description: 'Plus Hosting (Yearly)', quantity: 1, unit_price: '', amount: 0 }],
                            is_recurring: true,
                            recurring_interval: 'yearly'
                          },
                          'plus-yearly-support': {
                            description: 'Plus Support (Yearly)',
                            line_items: [{ description: 'Plus Support (Yearly)', quantity: 1, unit_price: '', amount: 0 }],
                            is_recurring: true,
                            recurring_interval: 'yearly'
                          },
                          'plus-yearly-hosting-support': {
                            description: 'Plus Hosting + Support (Yearly)',
                            line_items: [
                              { description: 'Plus Hosting (Yearly)', quantity: 1, unit_price: '', amount: 0 },
                              { description: 'Plus Support (Yearly)', quantity: 1, unit_price: '', amount: 0 }
                            ],
                            is_recurring: true,
                            recurring_interval: 'yearly'
                          },
                          // PRO
                          'pro-monthly-hosting': {
                            description: 'PRO Hosting (Monthly)',
                            line_items: [{ description: 'PRO Hosting (Monthly)', quantity: 1, unit_price: '', amount: 0 }],
                            is_recurring: true,
                            recurring_interval: 'monthly'
                          },
                          'pro-monthly-support': {
                            description: 'PRO Support (Monthly)',
                            line_items: [{ description: 'PRO Support (Monthly)', quantity: 1, unit_price: '', amount: 0 }],
                            is_recurring: true,
                            recurring_interval: 'monthly'
                          },
                          'pro-monthly-hosting-support': {
                            description: 'PRO Hosting + Support (Monthly)',
                            line_items: [
                              { description: 'PRO Hosting (Monthly)', quantity: 1, unit_price: '', amount: 0 },
                              { description: 'PRO Support (Monthly)', quantity: 1, unit_price: '', amount: 0 }
                            ],
                            is_recurring: true,
                            recurring_interval: 'monthly'
                          },
                          'pro-yearly-hosting': {
                            description: 'PRO Hosting (Yearly)',
                            line_items: [{ description: 'PRO Hosting (Yearly)', quantity: 1, unit_price: '', amount: 0 }],
                            is_recurring: true,
                            recurring_interval: 'yearly'
                          },
                          'pro-yearly-support': {
                            description: 'PRO Support (Yearly)',
                            line_items: [{ description: 'PRO Support (Yearly)', quantity: 1, unit_price: '', amount: 0 }],
                            is_recurring: true,
                            recurring_interval: 'yearly'
                          },
                          'pro-yearly-hosting-support': {
                            description: 'PRO Hosting + Support (Yearly)',
                            line_items: [
                              { description: 'PRO Hosting (Yearly)', quantity: 1, unit_price: '', amount: 0 },
                              { description: 'PRO Support (Yearly)', quantity: 1, unit_price: '', amount: 0 }
                            ],
                            is_recurring: true,
                            recurring_interval: 'yearly'
                          }
                        };
                        
                        const template = templates[templateId];
                        if (template) {
                          setFormData({
                            ...formData,
                            description: template.description,
                            line_items: template.line_items,
                            is_recurring: template.is_recurring,
                            recurring_interval: template.recurring_interval
                          });
                        }
                      }}
                    >
                      <SelectTrigger data-testid="invoice-template-select" className="text-sm">
                        <SelectValue placeholder="Select a template or enter manually" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="custom" className="text-sm text-slate-gray italic">Custom (Manual Entry)</SelectItem>
                        
                        <div className="px-2 py-1 text-xs text-slate-gray border-t mt-1 font-semibold bg-purple-50">Essentials</div>
                        <SelectItem value="essentials-monthly-hosting" className="text-sm">Essentials Hosting (Monthly)</SelectItem>
                        <SelectItem value="essentials-monthly-support" className="text-sm">Essentials Support (Monthly)</SelectItem>
                        <SelectItem value="essentials-monthly-hosting-support" className="text-sm">Essentials Hosting + Support (Monthly)</SelectItem>
                        <SelectItem value="essentials-yearly-hosting" className="text-sm">Essentials Hosting (Yearly)</SelectItem>
                        <SelectItem value="essentials-yearly-support" className="text-sm">Essentials Support (Yearly)</SelectItem>
                        <SelectItem value="essentials-yearly-hosting-support" className="text-sm">Essentials Hosting + Support (Yearly)</SelectItem>
                        
                        <div className="px-2 py-1 text-xs text-slate-gray border-t mt-1 font-semibold bg-blue-50">Plus</div>
                        <SelectItem value="plus-monthly-hosting" className="text-sm">Plus Hosting (Monthly)</SelectItem>
                        <SelectItem value="plus-monthly-support" className="text-sm">Plus Support (Monthly)</SelectItem>
                        <SelectItem value="plus-monthly-hosting-support" className="text-sm">Plus Hosting + Support (Monthly)</SelectItem>
                        <SelectItem value="plus-yearly-hosting" className="text-sm">Plus Hosting (Yearly)</SelectItem>
                        <SelectItem value="plus-yearly-support" className="text-sm">Plus Support (Yearly)</SelectItem>
                        <SelectItem value="plus-yearly-hosting-support" className="text-sm">Plus Hosting + Support (Yearly)</SelectItem>
                        
                        <div className="px-2 py-1 text-xs text-slate-gray border-t mt-1 font-semibold bg-amber-50">PRO</div>
                        <SelectItem value="pro-monthly-hosting" className="text-sm">PRO Hosting (Monthly)</SelectItem>
                        <SelectItem value="pro-monthly-support" className="text-sm">PRO Support (Monthly)</SelectItem>
                        <SelectItem value="pro-monthly-hosting-support" className="text-sm">PRO Hosting + Support (Monthly)</SelectItem>
                        <SelectItem value="pro-yearly-hosting" className="text-sm">PRO Hosting (Yearly)</SelectItem>
                        <SelectItem value="pro-yearly-support" className="text-sm">PRO Support (Yearly)</SelectItem>
                        <SelectItem value="pro-yearly-hosting-support" className="text-sm">PRO Hosting + Support (Yearly)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-gray mt-1">Select a template to auto-fill or enter details manually below</p>
                  </div>
                  
                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-carbon-black mb-2">Description *</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => {
                        const newDescription = e.target.value;
                        const newLineItems = [...formData.line_items];
                        // Auto-update first line item description to match
                        if (newLineItems.length > 0) {
                          newLineItems[0] = { ...newLineItems[0], description: newDescription };
                        }
                        setFormData({ ...formData, description: newDescription, line_items: newLineItems });
                      }}
                      className="w-full px-3 sm:px-4 py-2 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue text-sm"
                      rows="2"
                      required
                      data-testid="invoice-description-input"
                      placeholder="e.g., Website Development - Phase 1"
                    />
                  </div>
                  
                  {/* Due Date */}
                  <div>
                    <label className="block text-sm font-semibold text-carbon-black mb-2">Due Date</label>
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue text-sm"
                      data-testid="invoice-due-date-input"
                    />
                  </div>

                  {/* Recurring Options */}
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Repeat className="h-5 w-5 text-fearless-blue" />
                      <label className="text-sm font-semibold text-carbon-black">Recurring Invoice</label>
                    </div>
                    
                    <div className="flex items-center gap-3 mb-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_recurring}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            is_recurring: e.target.checked,
                            recurring_interval: e.target.checked ? 'monthly' : ''
                          })}
                          className="w-4 h-4 rounded border-gray-300 text-fearless-blue focus:ring-fearless-blue"
                          data-testid="invoice-recurring-checkbox"
                        />
                        <span className="text-sm text-slate-gray">Make this a recurring invoice</span>
                      </label>
                    </div>
                    
                    {formData.is_recurring && (
                      <div className="space-y-3 pt-2 border-t border-purple-100">
                        <div>
                          <label className="block text-xs font-medium text-slate-gray mb-1">Billing Cycle</label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, recurring_interval: 'monthly' })}
                              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                                formData.recurring_interval === 'monthly'
                                  ? 'bg-fearless-blue text-white shadow-md'
                                  : 'bg-white border border-gray-200 text-slate-gray hover:border-fearless-blue'
                              }`}
                              data-testid="recurring-monthly-btn"
                            >
                              <CalendarClock className="h-4 w-4 inline mr-1" />
                              Monthly
                            </button>
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, recurring_interval: 'yearly' })}
                              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                                formData.recurring_interval === 'yearly'
                                  ? 'bg-fearless-blue text-white shadow-md'
                                  : 'bg-white border border-gray-200 text-slate-gray hover:border-fearless-blue'
                              }`}
                              data-testid="recurring-yearly-btn"
                            >
                              <CalendarClock className="h-4 w-4 inline mr-1" />
                              Yearly
                            </button>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-slate-gray mb-1">End Date (Optional)</label>
                          <input
                            type="date"
                            value={formData.recurring_end_date}
                            onChange={(e) => setFormData({ ...formData, recurring_end_date: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                            data-testid="recurring-end-date-input"
                          />
                          <p className="text-xs text-slate-gray mt-1">Leave empty to continue indefinitely</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Line Items */}
                  <div>
                    <label className="block text-sm font-semibold text-carbon-black mb-2">Line Items *</label>
                    <div className="space-y-2">
                      {formData.line_items.map((item, index) => (
                        <div key={index} className="flex gap-2 items-start p-2 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <input
                              type="text"
                              placeholder="Description"
                              value={item.description}
                              onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                              className="w-full px-2 py-1.5 rounded border border-gray-200 text-sm"
                              required
                            />
                          </div>
                          <div className="w-16">
                            <input
                              type="number"
                              placeholder="Qty"
                              value={item.quantity}
                              onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                              className="w-full px-2 py-1.5 rounded border border-gray-200 text-sm text-center"
                              min="1"
                              step="1"
                            />
                          </div>
                          <div className="w-24">
                            <input
                              type="number"
                              placeholder="Price"
                              value={item.unit_price || ''}
                              onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                              className="w-full px-2 py-1.5 rounded border border-gray-200 text-sm"
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <div className="w-24 text-right py-1.5 font-medium text-sm">
                            ${formatCurrency(item.amount || 0)}
                          </div>
                          {formData.line_items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLineItem(index)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="mt-2">
                      <Plus className="h-4 w-4 mr-1" /> Add Line
                    </Button>
                  </div>
                  
                  {/* Total */}
                  <div className="flex justify-between items-center p-3 bg-gray-100 rounded-lg">
                    <span className="font-semibold">Total Amount:</span>
                    <span className="text-xl font-bold text-fearless-blue">${formatCurrency(calculateTotal())}</span>
                  </div>
                  
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>
                      Cancel
                    </Button>
                    <Button type="submit" className="gradient-fearless text-white" data-testid="invoice-submit-btn">
                      Create Invoice
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Invoice Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          <Card className="shadow-lg rounded-xl border-0">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-slate-gray font-medium mb-1 truncate">Total Invoiced</p>
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-carbon-black">${formatCurrency(totalAmount)}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                  <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-fearless-blue" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg rounded-xl border-0">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-slate-gray font-medium mb-1 truncate">Paid</p>
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-carbon-black">${formatCurrency(paidAmount)}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                  <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg rounded-xl border-0">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-slate-gray font-medium mb-1 truncate">Pending</p>
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-carbon-black">${formatCurrency(pendingAmount)}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                  <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters - Only show full filter options for admin */}
        <Card className="shadow-lg rounded-xl border-0">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-gray" />
                <span className="text-sm font-medium text-slate-gray">Filters:</span>
              </div>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32 text-sm h-9">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {user?.role === 'admin' && <SelectItem value="draft">Draft</SelectItem>}
                  {user?.role === 'admin' ? (
                    <SelectItem value="sent">Sent</SelectItem>
                  ) : (
                    <SelectItem value="sent">New</SelectItem>
                  )}
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="past_due">Past Due</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterSubscription} onValueChange={setFilterSubscription}>
                <SelectTrigger className="w-36 text-sm h-9">
                  <SelectValue placeholder="Subscription" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Invoices</SelectItem>
                  <SelectItem value="active">Active Subs</SelectItem>
                  <SelectItem value="inactive">Inactive Subs</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterProject} onValueChange={setFilterProject}>
                <SelectTrigger className="w-40 text-sm h-9">
                  <SelectValue placeholder="Project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.project_number ? `${project.project_number} - ${project.title}` : project.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {user?.role === 'admin' && clients.length > 0 && (
                <Select value={filterClient} onValueChange={setFilterClient}>
                  <SelectTrigger className="w-36 text-sm h-9">
                    <SelectValue placeholder="Client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-gray">
                  <X className="h-4 w-4 mr-1" /> Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Invoices List */}
        <Card className="shadow-lg rounded-xl border-0">
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-carbon-black" data-testid="invoices-list-title">
              {hasActiveFilters ? `Filtered Invoices (${invoices.length})` : `All Invoices (${invoices.length})`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <p className="text-slate-gray text-center py-8 text-sm sm:text-base">
                {hasActiveFilters ? 'No invoices match your filters.' : 'No invoices yet.'}
              </p>
            ) : (
              <div className="space-y-3 sm:space-y-4" key={`invoice-list-${listUpdateKey}`}>
                {invoices.map((invoice) => (
                  <div
                    key={`${invoice.id}-${invoice.viewed_at || 'unviewed'}`}
                    className="p-3 sm:p-4 lg:p-6 bg-cloud-white rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    data-testid={`invoice-item-${invoice.id}`}
                    onClick={() => viewInvoiceDetail(invoice)}
                  >
                    {/* Mobile Layout */}
                    <div className="sm:hidden">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-start space-x-3 min-w-0 flex-1">
                          {getStatusIcon(invoice)}
                          <div className="min-w-0">
                            <h3 className="font-bold text-carbon-black text-sm truncate">{invoice.invoice_number}</h3>
                            <p className="text-xs text-slate-gray truncate">{invoice.client_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          {getSeenIndicator(invoice)}
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusClass(invoice)}`}>
                            {getStatusLabel(invoice)}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-gray mb-2 line-clamp-2">{invoice.description}</p>
                      <div className="text-xs text-slate-gray mb-2 space-y-1">
                        <p className="truncate">Project: {invoice.project_title}</p>
                        {invoice.created_at && (
                          <p>Created: {new Date(invoice.created_at).toLocaleDateString('en-US')}</p>
                        )}
                        {invoice.due_date && (
                          <p>Due: {new Date(invoice.due_date).toLocaleDateString('en-US')}</p>
                        )}
                        {invoice.is_recurring && (
                          <p className="flex items-center text-purple-600">
                            <Repeat className="h-3 w-3 mr-1" />
                            Recurring: {invoice.recurring_interval === 'monthly' ? 'Monthly' : 'Yearly'}
                            {invoice.subscription_status && (
                              <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                invoice.subscription_status === 'active' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {invoice.subscription_status === 'active' ? 'Active' : 'Inactive'}
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <div className="font-bold text-carbon-black">
                          ${formatCurrency(invoice.amount)}
                        </div>
                        <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                          {/* 3-dot menu for admin actions */}
                          {user?.role === 'admin' && invoiceView === 'active' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-8 w-8 p-0"
                                  onClick={(e) => e.preventDefault()}
                                  data-testid={`invoice-menu-${invoice.id}`}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                {invoice.status === 'draft' && (
                                  <>
                                    <DropdownMenuItem onClick={() => openEditModal(invoice)} className="cursor-pointer">
                                      <Pencil className="h-4 w-4 mr-2 text-slate-gray" />
                                      <span>Edit Invoice</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => sendInvoice(invoice.id)} className="cursor-pointer">
                                      <Send className="h-4 w-4 mr-2 text-fearless-blue" />
                                      <span>Send Invoice</span>
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {invoice.status === 'sent' && (
                                  <>
                                    <DropdownMenuItem onClick={() => openEditModal(invoice)} className="cursor-pointer">
                                      <Pencil className="h-4 w-4 mr-2 text-slate-gray" />
                                      <span>Edit Invoice</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => markAsPaid(invoice.id)} className="cursor-pointer">
                                      <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                      <span>Mark as Paid</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => resendInvoice(invoice.id)} className="cursor-pointer">
                                      <RefreshCw className="h-4 w-4 mr-2 text-slate-gray" />
                                      <span>Resend Invoice</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => copyPaymentLink(invoice.id)} className="cursor-pointer">
                                      <Link2 className="h-4 w-4 mr-2 text-slate-gray" />
                                      <span>Copy Payment Link</span>
                                    </DropdownMenuItem>
                                  </>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => archiveInvoice(invoice.id)} className="cursor-pointer">
                                  <Archive className="h-4 w-4 mr-2 text-blue-600" />
                                  <span>Archive Invoice</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => deleteInvoice(invoice.id)} className="cursor-pointer text-red-600 focus:text-red-600">
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  <span>Delete Invoice</span>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {/* Archived view - restore option */}
                          {user?.role === 'admin' && invoiceView === 'archived' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => restoreInvoice(invoice.id)} className="cursor-pointer">
                                  <ArchiveRestore className="h-4 w-4 mr-2 text-green-600" />
                                  <span>Restore Invoice</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => permanentlyDeleteInvoice(invoice.id)} className="cursor-pointer text-red-600 focus:text-red-600">
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  <span>Delete Forever</span>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {/* Trash view - restore option */}
                          {user?.role === 'admin' && invoiceView === 'trash' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => restoreInvoice(invoice.id)} className="cursor-pointer">
                                  <ArchiveRestore className="h-4 w-4 mr-2 text-green-600" />
                                  <span>Restore Invoice</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => permanentlyDeleteInvoice(invoice.id)} className="cursor-pointer text-red-600 focus:text-red-600">
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  <span>Delete Forever</span>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {user?.role !== 'admin' && invoice.status === 'sent' && (
                            <Button
                              size="sm"
                              className="gradient-fearless text-white text-xs h-7 px-3"
                              onClick={() => payInvoice(invoice.id)}
                              disabled={paymentLoadingId === invoice.id}
                              data-testid={`pay-invoice-btn-${invoice.id}`}
                            >
                              {paymentLoadingId === invoice.id ? (
                                <><RefreshCw className="h-3 w-3 mr-1 animate-spin" /> Processing...</>
                              ) : (
                                <><DollarSign className="h-3 w-3 mr-1" /> Pay Now</>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden sm:flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1 min-w-0">
                        {getStatusIcon(invoice)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-1 flex-wrap">
                            <h3 className="font-bold text-carbon-black">{invoice.invoice_number}</h3>
                            <span className="text-sm text-slate-gray">•</span>
                            <span className="text-sm text-slate-gray truncate">{invoice.client_name}</span>
                          </div>
                          <p className="text-sm text-slate-gray mb-2 line-clamp-1">{invoice.description}</p>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-gray">
                            <span className="truncate">Project: {invoice.project_title}</span>
                            {invoice.created_at && (
                              <span>Created: {new Date(invoice.created_at).toLocaleDateString('en-US')}</span>
                            )}
                            {invoice.due_date && (
                              <span>Due: {new Date(invoice.due_date).toLocaleDateString('en-US')}</span>
                            )}
                            {invoice.paid_at && (
                              <span className="text-green-600">Paid: {new Date(invoice.paid_at).toLocaleDateString('en-US')}</span>
                            )}
                            {invoice.is_recurring && (
                              <span className="flex items-center text-purple-600">
                                <Repeat className="h-3 w-3 mr-1" />
                                {invoice.recurring_interval === 'monthly' ? 'Monthly' : 'Yearly'}
                                {invoice.subscription_status && (
                                  <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                    invoice.subscription_status === 'active' 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-red-100 text-red-700'
                                  }`}>
                                    {invoice.subscription_status === 'active' ? 'Active' : 'Inactive'}
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4 flex-shrink-0 ml-4" onClick={(e) => e.stopPropagation()}>
                        <div className="text-right">
                          <div className="font-bold text-carbon-black text-lg">
                            ${formatCurrency(invoice.amount)}
                          </div>
                        </div>
                        <div className="flex flex-col space-y-2 items-end">
                          <div className="flex items-center space-x-2">
                            {getSeenIndicator(invoice)}
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusClass(invoice)}`}>
                              {getStatusLabel(invoice)}
                            </span>
                          </div>
                          <div className="flex gap-2 items-center">
                            {/* Active View Actions - Desktop */}
                            {invoiceView === 'active' && (
                              <>
                                {/* Admin 3-dot menu for desktop */}
                                {user?.role === 'admin' && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="h-8 w-8 p-0"
                                        onClick={(e) => e.preventDefault()}
                                        data-testid={`invoice-menu-desktop-${invoice.id}`}
                                      >
                                        <MoreVertical className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48">
                                      {invoice.status === 'draft' && (
                                        <>
                                          <DropdownMenuItem onClick={() => openEditModal(invoice)} className="cursor-pointer">
                                            <Pencil className="h-4 w-4 mr-2 text-slate-gray" />
                                            <span>Edit Invoice</span>
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => sendInvoice(invoice.id)} className="cursor-pointer">
                                            <Send className="h-4 w-4 mr-2 text-fearless-blue" />
                                            <span>Send Invoice</span>
                                          </DropdownMenuItem>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem onClick={() => deleteInvoice(invoice.id)} className="cursor-pointer text-red-600 focus:text-red-600">
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            <span>Delete Invoice</span>
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                      {invoice.status === 'sent' && (
                                        <>
                                          <DropdownMenuItem onClick={() => openEditModal(invoice)} className="cursor-pointer">
                                            <Pencil className="h-4 w-4 mr-2 text-slate-gray" />
                                            <span>Edit Invoice</span>
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => markAsPaid(invoice.id)} className="cursor-pointer">
                                            <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                            <span>Mark as Paid</span>
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => resendInvoice(invoice.id)} className="cursor-pointer">
                                            <RefreshCw className="h-4 w-4 mr-2 text-slate-gray" />
                                            <span>Resend Invoice</span>
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => copyPaymentLink(invoice.id)} className="cursor-pointer">
                                            <Link2 className="h-4 w-4 mr-2 text-slate-gray" />
                                            <span>Copy Payment Link</span>
                                          </DropdownMenuItem>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem onClick={() => archiveInvoice(invoice.id)} className="cursor-pointer">
                                            <Archive className="h-4 w-4 mr-2 text-blue-600" />
                                            <span>Archive Invoice</span>
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => deleteInvoice(invoice.id)} className="cursor-pointer text-red-600 focus:text-red-600">
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            <span>Delete Invoice</span>
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                      {invoice.status === 'paid' && (
                                        <DropdownMenuItem onClick={() => archiveInvoice(invoice.id)} className="cursor-pointer">
                                          <Archive className="h-4 w-4 mr-2 text-blue-600" />
                                          <span>Archive Invoice</span>
                                        </DropdownMenuItem>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                                {/* User Pay Now button */}
                                {user?.role !== 'admin' && invoice.status === 'sent' && (
                                  <Button
                                    size="sm"
                                    className="gradient-fearless text-white text-xs"
                                    onClick={() => payInvoice(invoice.id)}
                                    disabled={paymentLoadingId === invoice.id}
                                    data-testid={`pay-invoice-btn-desktop-${invoice.id}`}
                                  >
                                    {paymentLoadingId === invoice.id ? (
                                      <><RefreshCw className="h-3 w-3 mr-1 animate-spin" /> Processing...</>
                                    ) : (
                                      <><DollarSign className="h-3 w-3 mr-1" /> Pay Now</>
                                    )}
                                  </Button>
                                )}
                              </>
                            )}
                            {/* Archived View Actions - Desktop */}
                            {invoiceView === 'archived' && user?.role === 'admin' && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                  <DropdownMenuItem onClick={() => restoreInvoice(invoice.id)} className="cursor-pointer">
                                    <ArchiveRestore className="h-4 w-4 mr-2 text-green-600" />
                                    <span>Restore Invoice</span>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => deleteInvoice(invoice.id)} className="cursor-pointer text-red-600 focus:text-red-600">
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    <span>Move to Trash</span>
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                            {/* Trash View Actions - Desktop */}
                            {invoiceView === 'trash' && user?.role === 'admin' && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                  <DropdownMenuItem onClick={() => restoreInvoice(invoice.id)} className="cursor-pointer">
                                    <ArchiveRestore className="h-4 w-4 mr-2 text-green-600" />
                                    <span>Restore Invoice</span>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => permanentlyDeleteInvoice(invoice.id)} className="cursor-pointer text-red-600 focus:text-red-600">
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    <span>Delete Forever</span>
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invoice Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={(open) => {
        setShowDetailModal(open);
        // Navigate back to invoices list when closing if we came from a direct link
        if (!open && urlInvoiceId) {
          navigate('/dashboard/invoices');
        }
      }}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedInvoice && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>Invoice {selectedInvoice.invoice_number}</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusClass(selectedInvoice)}`}>
                    {getStatusLabel(selectedInvoice)}
                  </span>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Invoice Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-gray">Project</p>
                    <p className="font-medium">
                      {selectedInvoice.project_title}
                      {selectedInvoice.invoice_type === 'other_services' && (
                        <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Other Services</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-gray">Client</p>
                    <p className="font-medium">{selectedInvoice.client_name}</p>
                    {selectedInvoice.client_company && (
                      <p className="text-xs text-slate-gray">{selectedInvoice.client_company}</p>
                    )}
                    {selectedInvoice.client_email && (
                      <p className="text-xs text-slate-gray">{selectedInvoice.client_email}</p>
                    )}
                    {selectedInvoice.client_phone && (
                      <p className="text-xs text-slate-gray">{selectedInvoice.client_phone}</p>
                    )}
                  </div>
                  {selectedInvoice.due_date && (
                    <div>
                      <p className="text-xs text-slate-gray">Due Date</p>
                      <p className="font-medium">{new Date(selectedInvoice.due_date).toLocaleDateString('en-US')}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-slate-gray">Created</p>
                    <p className="font-medium">{new Date(selectedInvoice.created_at).toLocaleDateString('en-US')}</p>
                  </div>
                </div>
                
                {/* Description */}
                <div>
                  <p className="text-xs text-slate-gray mb-1">Description</p>
                  <p className="text-sm">{selectedInvoice.description}</p>
                </div>
                
                {/* Line Items */}
                {(selectedInvoice.line_items?.length > 0 || selectedInvoice.items?.length > 0) && (
                  <div>
                    <p className="text-xs text-slate-gray mb-2">Line Items</p>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-2">Description</th>
                            <th className="text-right p-2 w-16">Qty</th>
                            <th className="text-right p-2 w-24">Price</th>
                            <th className="text-right p-2 w-24">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedInvoice.line_items || selectedInvoice.items || []).map((item, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="p-2">{item.description}</td>
                              <td className="p-2 text-right">{item.quantity || 1}</td>
                              <td className="p-2 text-right">${formatCurrency(item.unit_price || 0)}</td>
                              <td className="p-2 text-right">${formatCurrency(item.amount || 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Total */}
                <div className="flex justify-between items-center p-4 bg-gray-100 rounded-lg">
                  <span className="font-semibold text-lg">Total Amount</span>
                  <span className="text-2xl font-bold text-fearless-blue">${formatCurrency(selectedInvoice.amount)}</span>
                </div>
                
                {/* Recurring/Subscription Info */}
                {selectedInvoice.is_recurring && (
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-100">
                    <div className="flex items-center text-purple-700">
                      <Repeat className="h-4 w-4 mr-2" />
                      <span className="font-medium">
                        Recurring: {selectedInvoice.recurring_interval === 'monthly' ? 'Monthly' : 'Yearly'}
                      </span>
                    </div>
                    {selectedInvoice.subscription_status && (
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        selectedInvoice.subscription_status === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        Subscription {selectedInvoice.subscription_status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Actions */}
                <DialogFooter className="flex flex-wrap gap-2">
                  {user?.role === 'admin' && selectedInvoice.status === 'draft' && (
                    <>
                      <Button variant="outline" onClick={() => { sendInvoice(selectedInvoice.id); setShowDetailModal(false); }}>
                        <Send className="h-4 w-4 mr-2" /> Send Invoice
                      </Button>
                      <Button variant="destructive" onClick={() => { deleteInvoice(selectedInvoice.id); setShowDetailModal(false); }}>
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </Button>
                    </>
                  )}
                  {user?.role === 'admin' && selectedInvoice.status === 'sent' && (
                    <>
                      <Button variant="outline" onClick={() => { resendInvoice(selectedInvoice.id); }}>
                        <RefreshCw className="h-4 w-4 mr-2" /> Resend
                      </Button>
                      <Button variant="outline" onClick={() => copyPaymentLink(selectedInvoice.id)}>
                        <Link2 className="h-4 w-4 mr-2" /> Copy Link
                      </Button>
                      <Button variant="outline" onClick={() => { updateInvoiceStatus(selectedInvoice.id, 'paid'); setShowDetailModal(false); }}>
                        <CheckCircle className="h-4 w-4 mr-2" /> Mark as Paid
                      </Button>
                    </>
                  )}
                  {user?.role !== 'admin' && selectedInvoice.status === 'sent' && (
                    <Button
                      className="gradient-fearless text-white"
                      onClick={() => payInvoice(selectedInvoice.id)}
                      disabled={paymentLoadingId === selectedInvoice.id}
                    >
                      {paymentLoadingId === selectedInvoice.id ? (
                        <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                      ) : (
                        <><DollarSign className="h-4 w-4 mr-2" /> Pay Now</>
                      )}
                    </Button>
                  )}
                  <Button variant="ghost" onClick={() => setShowDetailModal(false)}>
                    Close
                  </Button>
                </DialogFooter>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => { 
        setShowEditModal(open); 
        if (!open) setEditingInvoice(null); 
      }}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">
              Edit Invoice {editingInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {editingInvoice && (
            <form onSubmit={handleEditSubmit} className="space-y-4" data-testid="edit-invoice-form">
              {/* Client Info (Read-only) */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 space-y-1">
                <p className="text-xs text-blue-600 font-medium uppercase tracking-wide">Client Info</p>
                <p className="text-sm text-blue-800"><strong>Name:</strong> {editingInvoice.client_name || 'N/A'}</p>
                <p className="text-sm text-blue-800"><strong>Email:</strong> {editingInvoice.client_email || 'N/A'}</p>
                {editingInvoice.project_title && (
                  <p className="text-sm text-blue-800"><strong>Project:</strong> {editingInvoice.project_title}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-carbon-black mb-2">Description *</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue text-sm"
                  rows="2"
                  required
                  data-testid="edit-invoice-description"
                  placeholder="Invoice description"
                />
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-sm font-semibold text-carbon-black mb-2">Due Date</label>
                <input
                  type="date"
                  value={editFormData.due_date}
                  onChange={(e) => setEditFormData({ ...editFormData, due_date: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue text-sm"
                  data-testid="edit-invoice-due-date"
                />
              </div>

              {/* Recurring Options */}
              <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
                <div className="flex items-center gap-2 mb-3">
                  <Repeat className="h-5 w-5 text-fearless-blue" />
                  <label className="text-sm font-semibold text-carbon-black">Recurring Invoice</label>
                </div>
                
                <div className="flex items-center gap-3 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editFormData.is_recurring}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        is_recurring: e.target.checked,
                        recurring_interval: e.target.checked ? (editFormData.recurring_interval || 'monthly') : ''
                      })}
                      className="w-4 h-4 rounded border-gray-300 text-fearless-blue focus:ring-fearless-blue"
                      data-testid="edit-invoice-recurring-checkbox"
                    />
                    <span className="text-sm text-slate-gray">Make this a recurring invoice</span>
                  </label>
                </div>
                
                {editFormData.is_recurring && (
                  <div className="space-y-3 pt-2 border-t border-purple-100">
                    <div>
                      <label className="block text-xs font-medium text-slate-gray mb-1">Billing Cycle</label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setEditFormData({ ...editFormData, recurring_interval: 'monthly' })}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                            editFormData.recurring_interval === 'monthly'
                              ? 'bg-fearless-blue text-white shadow-md'
                              : 'bg-white border border-gray-200 text-slate-gray hover:border-fearless-blue'
                          }`}
                        >
                          <CalendarClock className="h-4 w-4 inline mr-1" />
                          Monthly
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditFormData({ ...editFormData, recurring_interval: 'yearly' })}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                            editFormData.recurring_interval === 'yearly'
                              ? 'bg-fearless-blue text-white shadow-md'
                              : 'bg-white border border-gray-200 text-slate-gray hover:border-fearless-blue'
                          }`}
                        >
                          <CalendarClock className="h-4 w-4 inline mr-1" />
                          Yearly
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-slate-gray mb-1">End Date (Optional)</label>
                      <input
                        type="date"
                        value={editFormData.recurring_end_date}
                        onChange={(e) => setEditFormData({ ...editFormData, recurring_end_date: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Line Items */}
              <div>
                <label className="block text-sm font-semibold text-carbon-black mb-2">Line Items *</label>
                <div className="space-y-2">
                  {editFormData.line_items.map((item, index) => (
                    <div key={index} className="flex gap-2 items-start p-2 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Description"
                          value={item.description}
                          onChange={(e) => updateEditLineItem(index, 'description', e.target.value)}
                          className="w-full px-2 py-1.5 rounded border border-gray-200 text-sm"
                          required
                        />
                      </div>
                      <div className="w-16">
                        <input
                          type="number"
                          placeholder="Qty"
                          value={item.quantity}
                          onChange={(e) => updateEditLineItem(index, 'quantity', e.target.value)}
                          className="w-full px-2 py-1.5 rounded border border-gray-200 text-sm text-center"
                          min="1"
                          step="1"
                        />
                      </div>
                      <div className="w-24">
                        <input
                          type="number"
                          placeholder="Price"
                          value={item.unit_price || ''}
                          onChange={(e) => updateEditLineItem(index, 'unit_price', e.target.value)}
                          className="w-full px-2 py-1.5 rounded border border-gray-200 text-sm"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div className="w-24 text-right py-1.5 font-medium text-sm">
                        ${formatCurrency(item.amount || 0)}
                      </div>
                      {editFormData.line_items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEditLineItem(index)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addEditLineItem} className="mt-2">
                  <Plus className="h-4 w-4 mr-1" /> Add Line
                </Button>
              </div>

              {/* Total */}
              <div className="flex justify-between items-center p-3 bg-gray-100 rounded-lg">
                <span className="font-semibold">Total Amount:</span>
                <span className="text-xl font-bold text-fearless-blue">${formatCurrency(calculateEditTotal())}</span>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setShowEditModal(false); setEditingInvoice(null); }}>
                  Cancel
                </Button>
                <Button type="submit" className="gradient-fearless text-white" data-testid="edit-invoice-submit-btn">
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default InvoicesPage;
